import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SharedModule } from 'app/shared';
import { NewNotificationButtonComponent } from './new-notification-button.component';
import { NotificationsDropdownFooterComponent } from './notifications-dropdown-footer.component';
import { NotificationsDropdownHeaderComponent } from './notifications-dropdown-header.component';
import { NotificationsItemComponent } from './notifications-item.component';
import { changeOnlyUnread, requestChangeReadState, requestHideNotification } from './notifications.actions';
import { ReadState } from './notifications.model';
import { getLoadedState, getNotifications, getOnlyUnread, getUnreadCount } from './notifications.reducer';


@Component({
    selector: 'notifications-list',
    imports: [
        FormsModule,
        SharedModule,
        NewNotificationButtonComponent,
        NotificationsDropdownHeaderComponent,
        NotificationsDropdownFooterComponent,
        NotificationsItemComponent,
    ],
    styles: [
        `
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
        .content {
            display: flex;
            flex-direction: column;
            min-height: 0;
            flex: 1 1 100%;
        }
        .notifications-list-btn,
        .notifications-list-btn:hover {
            border: none;
            background-color: transparent;
        }
        .notifications-settings-icon {
            width: 16px;
            height: 16px;
            margin-top: -2px;
        }
        .notifications-link {
            color: #1783d6;
        }
        .notifications-list-btn:hover,
        button:hover .notifications-link,
        .notifications-link:hover {
            color: #01549B;
        }
        .toolbar {
            display: flex;
            align-items: baseline;
            font-size: 90%;
            color: #222729;
        }
        .toolbar button {
            border: none;
            background: transparent;
            border-radius: 5px;
        }
        .toolbar button:hover {
            background-color: #F5F5F5;
        }

        .notifications-list-no-data {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        .no-notifications-text {
            color:  #797979;
        }
        .no-notifications-img {
            width: 130px;
            height: auto;
        }
        @media (max-width: 512px) {
            new-notification-button {
                display: none;
            }
        }
        `,
    ],
    template: `
        <notifications-dropdown-header>
            Notifications {{ unreadCount() > 0 ? '(' + unreadCount() + ')' : '' }}
        </notifications-dropdown-header>
        @if (loaded()) {
            <div class="content">
                <div class="toolbar">
                    <div class="flex-grow-1">Recent 30 days</div>
                    <div class="d-flex align-items-center border-right">
                        <label class="mb-0 mr-2" for="onlyUnread">Only show unread</label>
                        <ui-toggle id="onlyUnread" [ngModel]="onlyUnread()" (ngModelChange)="changeOnlyUnread($event)"></ui-toggle>
                        <span class="ml-1" style="width: 2.5em">{{ onlyUnread() ? 'ON' : 'OFF' }}</span>
                    </div>
                    <div>
                        <button (click)="changeAllReadState(unreadCount() > 0)">
                            {{ unreadCount() > 0 ? 'Mark all as read' : 'Mark all as unread' }}
                        </button>
                    </div>
                </div>
                @if (notifications().length > 0) {
                    <div class="mt-2 flex-grow-1 flex-shrink-1 min-height-0" scrollable>
                        @for (notification of notifications(); track notification.notificationId) {
                            @if (!notification.hidden) {
                                <notifications-item
                                    animate.enter="fade-enter"
                                    animate.leave="fade-leave"
                                    style="--fade-duration: 0.25s"
                                    [notification]="notification"
                                    (changeNotificationReadState)="changeReadStatus($event)"
                                    (hideNotification)="hideNotification($event)"
                                    (openFolder)="openFolder($event)"
                                    (clickNotificationDetail)="closeNotifications()">
                                </notifications-item>
                            }
                        }
                    </div>
                }
                @else {
                    <div class="notifications-list-no-data">
                        <div class="no-notifications-text mt-6">
                            There are no new notifications right now...
                        </div>
                        <img class="no-notifications-img mt-5" src="/assets/images/no-notifications-img.svg" alt="No notifications">
                        <div class="no-notifications-text mt-5 mb-5">
                            Tip: Start by searching and selecting properties on the map
                        </div>
                        <new-notification-button></new-notification-button>
                    </div>
                }
            </div>
        }
        @else {
            <div class="h-100 d-flex flex-column justify-content-center">
                <ui-block-loading></ui-block-loading>
            </div>
        }
        <notifications-dropdown-footer>
            <button class="notifications-list-btn" (click)="goToPreferences()">
                <img src="/assets/images/settings-light-blue.svg" class="notifications-settings-icon mr-1 d-inline-block">
                <div class="notifications-link d-inline-block">
                    Notification Preferences
                </div>
            </button>
        </notifications-dropdown-footer>
    `,
})
export class NotificationsListComponent {

    private store = inject(Store);

    unreadCount = toSignal(this.store.select(getUnreadCount), { initialValue: 0 });

    onlyUnread = toSignal(this.store.select(getOnlyUnread), { initialValue: false });

    notifications = toSignal(this.store.select(getNotifications), { initialValue: [] });

    loaded = toSignal(this.store.select(getLoadedState), { initialValue: false });

    private router = inject(Router);

    goToPreferences() {
        this.router.navigate([{ outlets: { notifications: 'settings' } }]);
    }

    changeReadStatus(notification: ReadState) {
        this.store.dispatch(requestChangeReadState({ notifications: [notification] }));
    }

    changeOnlyUnread(unread: boolean) {
        this.store.dispatch(changeOnlyUnread({ unread }));
    }

    hideNotification(notificationId: string) {
        this.store.dispatch(requestHideNotification({ notificationId }));
    }

    openFolder(collectionId: string) {
        this.router.navigate(['/dashboard', 'saved-properties'], { queryParams: { collectionId } }).then(() => {
            this.router.navigate([{ outlets: { notifications: null } }]);
        });
    }

    changeAllReadState(read: boolean) {
        const notifications = this.notifications().filter(n => n.read !== read).map(({ notificationId }) => ({ notificationId, read }));
        this.store.dispatch(requestChangeReadState({ notifications }));
    }

    closeNotifications() {
        setTimeout(() => this.router.navigate([{ outlets: { notifications: null } }]));
    }
}
